import { useParams } from 'react-router'
import { motion } from 'motion/react'
import { usePage } from '@/lib/queries'
import { fileUrl } from '@/lib/pb'
import { plainText } from '@/lib/utils'
import { Container } from '@/components/site/primitives'
import { PageHero } from '@/components/site/page-hero'
import { LoadingBlock } from '@/components/ui/feedback'
import { usePageMeta } from '@/components/layout/site-layout'
import NotFoundPage from './not-found'

/**
 * Renders any page the club creates in the dashboard, at its own slug.
 *
 * `/story` and `/club` have dedicated routes because they compose extra
 * content — the timeline, the values grid, the staff and partner lists. Every
 * other page is just a masthead and prose, and needs no code: an editor creates
 * it, adds a nav link pointing at it, and it is live.
 *
 * This route is registered last among the single-segment paths, so the static
 * routes (/squad, /news, …) always win. A URL that matches neither a static
 * route nor a published page falls through to the 404.
 */
export default function CmsPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: page, isPending, isError } = usePage(slug ?? '')

  usePageMeta(page?.title, page?.seo_description || plainText(page?.body ?? '', 200))

  if (isPending) return <LoadingBlock className="min-h-[70vh] pt-[calc(var(--header-h)+4rem)]" />

  // A missing or unpublished page is an ordinary 404, not an error screen.
  if (isError || !page) return <NotFoundPage />

  return (
    <>
      <PageHero
        kicker={page.kicker}
        title={page.title}
        subtitle={page.subtitle}
        image={fileUrl(page, page.hero_image, '1600x0')}
      />

      {page.body ? (
        <section className="bg-ink py-14 md:py-24">
          <Container size="narrow">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="prose-viking text-[1.0625rem]"
              dangerouslySetInnerHTML={{ __html: page.body }}
            />
          </Container>
        </section>
      ) : null}
    </>
  )
}
